import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// maxSizeMB defaults to 10 — enough for a phone photo of a KK or ijazah,
// which routinely runs past the old 5MB cap. The Quran-recitation box passes
// its own (larger) limit; its "maks ±50MB" label used to be a lie because
// this component silently enforced 5MB for every caller.
export default function ResumeUpload({ label, accept, onChange, maxSizeMB = 10 }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const maxSize = maxSizeMB * 1024 * 1024;

  const handleChange = (e) => {
    const selectedFile = e.target.files?.[0];
    setError('');

    if (!selectedFile) {
      setFile(null);
      onChange(null);
      return;
    }

    if (selectedFile.size > maxSize) {
      setError(t('apply.fileTooLarge', { size: maxSizeMB, defaultValue: 'Berkas terlalu besar (maks {{size}}MB)' }));
      return;
    }

    setFile(selectedFile);
    onChange(selectedFile);
  };

  const handleRemove = () => {
    setFile(null);
    setError('');
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  // The label lives INSIDE the card. With it above, a title that wrapped to
  // two lines ("Driver's Licence (SIM)") pushed its box down and broke the
  // alignment of the whole row. h-full + a stretching drop zone now gives
  // every card in a grid row the same height whatever its title does.
  return (
    <div className="flex h-full flex-col">
      <div
        className={`flex h-full flex-col rounded-xl border-2 border-dashed p-4 transition-colors ${
          error ? 'border-red-300 bg-red-50/30'
            : file ? 'border-primary-300 bg-primary-50/40'
              : 'border-gray-300 hover:border-primary-300 hover:bg-gray-50'
        }`}
      >
        {/* min-h reserves two lines for every title, so a title that wraps
            ("Police Clearance (SKCK) *") doesn't push its upload icon lower
            than the icons beside it. */}
        <span className="mb-3 block min-h-[2.6rem] text-sm font-medium leading-snug text-gray-700">{label}</span>

        {file ? (
          <div className="mt-auto flex items-center justify-between gap-2 rounded-lg bg-white/80 px-3 py-2">
            <div className="flex min-w-0 items-center gap-2">
              <svg className="h-5 w-5 shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="truncate text-sm text-gray-700" title={file.name}>{file.name}</span>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              aria-label={t('common.delete')}
              className="shrink-0 text-red-500 hover:text-red-700"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <label className="flex flex-1 cursor-pointer flex-col items-center justify-center py-2 text-center">
            <svg className="mb-2 h-7 w-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span className="text-sm font-medium text-primary-600">{t('apply.uploadFile')}</span>
            <span className="mt-0.5 text-xs text-gray-500">{t('apply.maxSize', { size: maxSizeMB })}</span>
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              onChange={handleChange}
              className="hidden"
            />
          </label>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
